import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { UpdateReport } from 'src/api';
import { Button } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { UploadedEvidence } from 'src/components/molecules/UploadedEvidence/UploadedEvidence';
import {
  CaseReportDatePlace,
  CaseReportEventDescription,
  CaseReportEventNamedOffendersList,
  ReportEventContainer,
} from 'src/components/moleculesLegacy';
import { updateCurrentEvent } from 'src/creators/MCC.creators';
import { AppDispatch } from 'src/store';
import { CaseDocuments, MCCEventType, UploadScanStatus } from 'src/types';
import { returnDashIfNoValue } from 'src/utils';
import { ConfirmDialog } from '../ConfirmDialog';
import { ButtonContainer } from './DraftEvent.styles';
import { deleteEvent } from './deleteEvent';

interface Props {
  reportID: string;
  currentReport: UpdateReport | null;
  updateReport: (id: string, report: UpdateReport) => Promise<void>;
  event: MCCEventType;
  setIsAddingEvent: (isAddingEvent: boolean) => void;
  setIsEditingEvent: (isEditingEvent: boolean) => void;
  evidenceDetails?: Record<string, CaseDocuments>;
}

export const DraftEvent = ({
  reportID,
  event,
  currentReport,
  updateReport,
  setIsAddingEvent,
  setIsEditingEvent,
  evidenceDetails,
}: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const [showDeleteEventMessage, setShowDeleteEventMessage] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const deleteCurrentEvent = useCallback(async () => {
    if (currentReport) {
      await deleteEvent(event.id, currentReport, updateReport);
      if (isMounted.current) {
        setShowDeleteEventMessage(false);
      }
    }
  }, [currentReport, event.id, updateReport]);

  return (
    <ReportEventContainer id={event.id} created={event.created}>
      <ConfirmDialog
        onCancel={() => setShowDeleteEventMessage(false)}
        onConfirm={deleteCurrentEvent}
        open={showDeleteEventMessage}
        setOpen={setShowDeleteEventMessage}
        title={'Delete Event?'}
        body={'Permanently deletes all event data and any attached evidence.'}
        data-testid="delete-event-dialog"
      />
      <CaseReportDatePlace
        date={event.date.value}
        time={event.time.value}
        location={returnDashIfNoValue(event.location.value)}
        data-testid="event-date-and-location"
      />
      <CaseReportEventDescription
        content={returnDashIfNoValue(event.description.value)}
        title={'What Happened'}
        data-testid="event-description"
      />
      <CaseReportEventDescription
        content={returnDashIfNoValue(event.emotion.value)}
        title={'Emotional Impact'}
        data-testid="event-emotion"
      />
      <CaseReportEventDescription
        data-testid="event-witnesses"
        content={returnDashIfNoValue(event.witness.value)}
        title={'Witnesses'}
      />
      <UploadedEvidence
        files={event.files.map((file) => {
          const details =
            evidenceDetails?.[file.path] || evidenceDetails?.[file.id];
          const extension =
            details?.filename.split('.').pop() ||
            file.path.split('.').pop() ||
            '';
          return {
            dateAdded: new Date(file.created),
            status: (details?.scanStatus ??
              file.status ??
              UploadScanStatus.PENDING) as UploadScanStatus | null,
            fileExtension: extension,
            fileS3Path: file.path,
            size: details?.size,
            dataDeletedAt: null,
          };
        })}
        reportId={reportID}
        caseManagerView={true}
        baseFileName="Evidence"
        data-testid="event-evidence"
      />
      <CaseReportEventNamedOffendersList
        namedOffenders={event.perpetrators.map((perp) => ({
          id: perp.id ?? '',
          createdAt: perp.created,
          updatedAt: perp.updated,
          value: { name: perp.name },
        }))}
        data-testid="event-named-offenders"
      />
      <ButtonContainer>
        <Button
          data-testid="delete-event"
          onClick={() => setShowDeleteEventMessage(true)}
          variant="secondary"
          size="small"
          width="100px"
        >
          <StyledIconInButton
            size="small"
            position="left"
            className="uil uil-trash-alt"
          />
          Delete
        </Button>
        <Button
          data-testid="edit-event"
          onClick={() => {
            dispatch(updateCurrentEvent(event));
            setIsEditingEvent(true);
            setIsAddingEvent(true);
          }}
          variant="primary"
          size="small"
          width="100px"
        >
          <StyledIconInButton
            size="small"
            position="left"
            className="uil uil-edit-alt"
          />
          Edit
        </Button>
      </ButtonContainer>
    </ReportEventContainer>
  );
};
